import { request } from '@utils/http'
import type {
  CodeSendResponse,
  LoginCodeRequest,
  LoginRequest,
  LoginResponse,
  LoginVerifyRequest,
  PasswordResetCodeRequest,
  PasswordResetRequest,
  PasswordResetResponse,
  RegisterCodeRequest,
  RegisterRequest
} from './types/auth'
import { UserType } from '@/enums/auth'

// 兼容现有接口
export interface StaffLoginRequest {
  account: string
  password: string
  enterpriseId: string
}

export interface StaffLoginResponse {
  token: string
  id: string
  username: string
  phone: string
  email: string
  realName: string
  tenantId: string
  userType: number
  roleType: number
}

/**
 * Code换取Token的返回类型
 */
export interface ConvertCodeToTokenResponse {
  token: string
  userType: number
}

/**
 * 员工账号密码登录（兼容现有接口）
 */
export const tenantUserLogin = (params: StaffLoginRequest) => {
  return request.post<StaffLoginResponse>('/enterprise/tenant/user/login', params)
}

// ==================== 租户端认证API ====================

/**
 * 1. 注册发送验证码
 * @param params 请求参数 { phone }
 */
export const sendRegisterCode = (params: RegisterCodeRequest) => {
  return request.post<CodeSendResponse>('/enterprise/tenant/user/register/code', params)
}

/**
 * 2. 用户注册
 * @param params 注册参数
 */
export const userRegister = (params: RegisterRequest) => {
  return request.post<string>('/enterprise/tenant/user/register', params)
}

/**
 * 3. 用户密码登录
 * @param params 登录参数
 */
export const userLogin = (params: LoginRequest) => {
  return request.post<LoginResponse>('/enterprise/tenant/user/login', params)
}

/**
 * 4. 登录发送验证码
 * @param params 请求参数 { account }
 */
export const sendLoginCode = (params: LoginCodeRequest) => {
  return request.post<CodeSendResponse>('/enterprise/tenant/user/login/code', params)
}

/**
 * 5. 验证码登录
 * @param params 验证码登录参数
 */
export const userLoginVerify = (params: LoginVerifyRequest) => {
  return request.post<LoginResponse>('/enterprise/tenant/user/login/verify', params)
}

/**
 * 6. 密码重置发送验证码
 * @param params 请求参数 { account }
 */
export const sendPasswordResetCode = (params: PasswordResetCodeRequest) => {
  return request.post<CodeSendResponse>('/enterprise/tenant/user/password/reset/code', params)
}

/**
 * 7. 重置密码
 * @param params 重置密码参数
 */
export const resetPassword = (params: PasswordResetRequest) => {
  return request.post<PasswordResetResponse>('/enterprise/tenant/user/password/reset', params)
}

// ==================== 客户端认证API ====================

/**
 * 1. 客户端注册发送验证码
 * @param params 请求参数 { phone }
 */
export const clientSendRegisterCode = (params: RegisterCodeRequest) => {
  return request.post<CodeSendResponse>('/enterprise/client/user/register/code', params)
}

/**
 * 2. 客户端用户注册
 * @param params 注册参数
 */
export const clientUserRegister = (params: RegisterRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/register', params)
}

/**
 * 3. 客户端用户密码登录
 * @param params 登录参数
 */
export const clientUserLogin = (params: LoginRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/login', params)
}

/**
 * 4. 客户端登录发送验证码
 * @param params 请求参数 { account }
 */
export const clientSendLoginCode = (params: LoginCodeRequest) => {
  return request.post<CodeSendResponse>('/enterprise/client/user/login/code', params)
}

/**
 * 5. 客户端验证码登录
 * @param params 验证码登录参数
 */
export const clientUserLoginVerify = (params: LoginVerifyRequest) => {
  return request.post<LoginResponse>('/enterprise/client/user/login/verify', params)
}

/**
 * 6. 客户端密码重置发送验证码
 * @param params 请求参数 { account }
 */
export const clientSendPasswordResetCode = (params: PasswordResetCodeRequest) => {
  return request.post<string>('/enterprise/client/user/password/reset/code', params)
}

/**
 * 7. 客户端重置密码
 * @param params 重置密码参数
 */
export const clientResetPassword = (params: PasswordResetRequest) => {
  return request.post<boolean>('/enterprise/client/user/password/reset', params)
}

/**
 * Token转换为Code
 * @returns Promise<string> 返回6位Code
 */
export const convertTokenToCode = (): Promise<string> => {
  return request.post<string>('/enterprise/tenant/user/convertToken', {})
}

/**
 * Code换取Token
 * @param code 6位验证码
 * @returns Promise<ConvertCodeToTokenResponse> 返回Token和用户类型
 */
export const convertCodeToToken = (code: string): Promise<ConvertCodeToTokenResponse> => {
  return request.post<ConvertCodeToTokenResponse>('/enterprise/tenant/user/login/token', { code })
}

/**
 * 获取租户用户详情
 * @returns Promise<any> 用户详情
 */
export const getTenantUserDetail = (): Promise<any> => {
  return request.post<any>('/enterprise/tenant/user/detail')
}

/**
 * 获取客户端用户详情
 * @returns Promise<any> 用户详情
 */
export const getClientUserDetail = (): Promise<any> => {
  return request.post<any>('/enterprise/client/user/detail')
}

/**
 * SSO 登录
 * @param params SSO登录参数
 * @returns Promise<LoginSsoResponse>
 */
export const loginSso = (params: import('./types/auth').LoginSsoRequest) => {
  return request.post<import('./types/auth').LoginSsoResponse>('/enterprise/tenant/user/login/sso', params)
}


/**
 * SSO 登录验证账号
 * @param params SSO登录参数
 * @returns Promise<LoginSsoResponse>
 */
export const loginSsoVerify = (params: import('./types/auth').LoginSsoVerifyRequest) => {
  return request.post<Boolean>('/enterprise/tenant/user/login/sso/verify', params)
}


// 内部分流工具
function getApiPrefix() {
  // 根据当前类型区分
  return useAuthStore().user.userType === UserType.TENANT ? '/enterprise/tenant' : '/enterprise/client'
}

/**
 * 退出登陆
 */
export const userLogout = (): Promise<void> => {
  return request.post<void>(`${getApiPrefix()}/user/logout`)
}

/**
 * 构建匿名访问token的请求参数
 */
export interface BuildAnonymousTokenRequest {
  userType: number
}

/**
 * 构建匿名访问token
 * @param params 请求参数 { userType, tenantId }
 * @returns Promise<string> 返回匿名访问token
 */
export const buildAnonymousAccessToken = (params: BuildAnonymousTokenRequest): Promise<string> => {
  return request.get<string>('/enterprise/auth/anonymous-access/buildToken', params)
}
